import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { FaClock, FaPlay } from 'react-icons/fa';
import CountdownTimer from './CountdownTimer';
import { SparkleButton } from './SparkleEffect';
import './BootcampPricingSection.css';

const BootcampPricingSection = ({
  title = "Simple Pricing",
  saleEndTime,
  isLoadingPricing,
  getPricingDetails,
  pricingError,
  setSaleEndTime,
  className = "",
  onCtaClick,
  ctaText = "Join the AI Bootcamp",
  showCta = true,
  paymentPlans = [],
  selectedPlanKey = 'bootcamp-upfront',
  onPlanSelect
}) => {
  // Helper to format price for display
  const formatPlanPrice = (plan, useOriginal = false) => {
    if (!plan) return '$0';
    // Use originalAmount for strikethrough, otherwise use discounted amount
    const amountCents = useOriginal ? (plan.originalAmount || plan.amount) : plan.amount;
    if (!amountCents) return '$0';
    const amount = amountCents / 100;
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Helper to get plan display label
  const getPlanDisplayLabel = (plan) => {
    if (!plan) return '';
    const installments = plan.installments || plan.metadata?.installments || 1;
    if (installments === 1) {
      return 'Pay in Full';
    }
    return `${installments} Monthly Payments`;
  };

  // Helper to get plan subtitle with discount support
  const getPlanSubtitle = (plan) => {
    if (!plan) return '';
    const installments = plan.installments || plan.metadata?.installments || 1;
    const currentAmount = formatPlanPrice(plan, false);
    const originalAmount = formatPlanPrice(plan, true);
    const hasDiscount = plan.hasDiscount;

    if (installments === 1) {
      return hasDiscount
        ? { current: `${currentAmount} one-time`, original: `${originalAmount} one-time`, hasDiscount: true }
        : { current: `${currentAmount} one-time`, original: null, hasDiscount: false };
    }
    return hasDiscount
      ? { current: `${currentAmount}/month`, original: `${originalAmount}/month`, hasDiscount: true }
      : { current: `${currentAmount}/month`, original: null, hasDiscount: false };
  };
  return (
    <div className={`bootcamp-pricing-section ${className}`}>
      <Container>
        <Row className="justify-content-center">
          <Col lg={8} xl={6}>
            <div className="text-center">
              <h2 className="bootcamp-sales-section-title mb-4">
                <span className="bootcamp-sales-highlight">{title}</span>
              </h2>
              
              {/* Sale Countdown Timer - Only show when there's an active promotion */}
              {saleEndTime && (
                <div className="sale-countdown-container mb-4">
                  <div className="sale-countdown-header">
                    <FaClock className="me-2" />
                    <span className="sale-countdown-label">🔥 LIMITED TIME OFFER 🔥</span>
                  </div>
                  {saleEndTime && (
                    <div className="sale-end-date">
                      Sale ends {saleEndTime.toLocaleDateString('en-US', { 
                        weekday: 'long',
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      }) + ' at ' + saleEndTime.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </div>
                  )}
                  {saleEndTime && (
                    <CountdownTimer 
                      key={saleEndTime.getTime()}
                      endDate={saleEndTime}
                      initialHours={0}
                      initialMinutes={0}
                      initialSeconds={0}
                      className="bootcamp-sale-countdown"
                      onExpire={() => {
                        console.log('Promotion countdown expired, hiding timer');
                        setSaleEndTime(null);
                      }}
                    />
                  )}
                </div>
              )}
              
              {isLoadingPricing ? (
                <div className="bootcamp-pricing-display">
                  <div className="pricing-highlight">
                    <div className="pricing-main">
                      <div className="spinner-border text-primary me-2" role="status" style={{width: '1rem', height: '1rem'}}>
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Loading pricing...
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bootcamp-pricing-display">
                  {/* Payment Plan Options */}
                  {paymentPlans.length > 1 && onPlanSelect && (
                    <div className="payment-plan-selector mb-4">
                      <div className="payment-plan-options">
                        {paymentPlans.map((plan) => {
                          const isSelected = plan.planKey === selectedPlanKey;
                          const installments = plan.installments || plan.metadata?.installments || 1;
                          // Show "Most Flexible" badge on 5-payment plan
                          const showBadge = installments === 5;
                          const subtitleInfo = getPlanSubtitle(plan);
                          return (
                            <button
                              key={plan.planKey}
                              className={`payment-plan-option ${isSelected ? 'selected' : ''} ${subtitleInfo.hasDiscount ? 'has-discount' : ''}`}
                              onClick={() => onPlanSelect(plan.planKey)}
                              type="button"
                            >
                              {showBadge && (
                                <div className="plan-option-badge">Most Flexible</div>
                              )}
                              <div className="plan-option-label">{getPlanDisplayLabel(plan)}</div>
                              <div className="plan-option-price">
                                {subtitleInfo.hasDiscount && (
                                  <span className="original-price-strike">{subtitleInfo.original}</span>
                                )}
                                <span className={subtitleInfo.hasDiscount ? 'discounted-price' : ''}>{subtitleInfo.current}</span>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="pricing-highlight">
                    <div className="pricing-main">
                      {getPricingDetails().totalInitialCharge}
                    </div>
                    {/* AI Bootcamp pricing subtitle based on selected plan */}
                    {getPricingDetails().isInstallmentPlan ? (
                      <div className="pricing-subtitle">
                        {getPricingDetails().monthlyCharge}
                      </div>
                    ) : (
                      <div className="pricing-subtitle">
                        one-time payment
                      </div>
                    )}
                    {getPricingDetails().isDiscounted && (
                      <div className="pricing-discount">
                        <span className="original-price">{getPricingDetails().originalTotal}</span>
                        <span className="savings-badge">LIMITED TIME DISCOUNT</span>
                      </div>
                    )}
                  </div>
                  <div className="pricing-features mt-4">
                    <p className="text-muted">
                      🎓 Complete AI Bootcamp Access<br/>
                      🔒 30-day money-back guarantee
                    </p>
                  </div>

                  {/* CTA Button */}
                  {showCta && onCtaClick && (
                    <div className="pricing-cta mt-4">
                      <SparkleButton
                        size="lg"
                        className="bootcamp-pricing-cta-button"
                        onClick={onCtaClick}
                        disabled={isLoadingPricing}
                      >
                        <FaPlay className="me-2" />
                        {ctaText}
                      </SparkleButton>
                    </div>
                  )}
                </div>
              )}
              
              {pricingError && (
                <div className="pricing-error mt-3">
                  <small className="text-warning">
                    ⚠️ Having trouble loading pricing. Click above to continue with checkout.
                  </small>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BootcampPricingSection;
